/* Produktbilder auf der Produktseite.
   fs-zoom (2026-09-29): Zoom-Fenster, ersetzt Dawns product-modal. Markup: snippets/fs-pdp-zoom.liquid.
     Dawns modal-opener ruft show(button) auf, button trägt data-media-id.
     Handy (2026-10-01): Spreizen mit zwei Fingern bis 3×, Runterziehen schließt.
   fs-gallery-nav (2026-09-30): Pfeile + Trackpad-Wischen der Seitengalerie am Desktop, weicher Wechsel.
     Handy (2026-10-01): dieselben Pfeile blättern Dawns Wisch-Slider weiter.
     iPad (2026-10-02): ab 750 px gibt es Dawns Slider nicht – hier folgt das Bild selbst dem Finger.
     Markup: snippets/product-media-gallery.liquid.
   Sichtbarkeit hängt nur an Attributen – kein requestAnimationFrame, kein IntersectionObserver. */
(() => {
  if (customElements.get('fs-zoom')) return;

  const ZOOM = 2; // Klick bzw. Doppeltippen
  const ZOOM_MAX = 3; // Spreizen mit zwei Fingern (Originale 1000–1500 px – darüber wird es weich)
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  // Gleicher Verlauf wie --fs-zoom-slide-time/-ease in fs-pdp-zoom.css
  const SLIDE = { duration: 600, easing: 'cubic-bezier(0.45, 0, 0.2, 1)' };

  // Mausrad-/Trackpad-Deltas in Pixel
  const wheelDelta = (event, pageSize) => {
    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? pageSize : 1;
    return { dx: event.deltaX * unit, dy: event.deltaY * unit };
  };

  // Waagrecht wischen = ein Schritt. Ein Wisch löst genau einmal aus, auch wenn das
  // Trackpad danach noch eine Sekunde nachläuft. Das Nachlaufen wird nur schwächer –
  // steigen die Werte zweimal hintereinander, liegt ein neuer Finger auf.
  const wheelSwiper = (step) => {
    const w = { sum: 0, last: -1e9, lastDelta: 0, rise: 0, locked: false, fired: 0 };
    return (dx, now) => {
      const pause = now - w.last > 160;
      w.rise = Math.abs(dx) > Math.abs(w.lastDelta) + 1 ? w.rise + 1 : 0;
      const newSwipe = w.locked && now - w.fired > 300 && w.rise >= 2 && Math.abs(dx) > 8;
      if (pause || newSwipe) {
        w.sum = 0;
        w.locked = false;
      }
      w.last = now;
      w.lastDelta = dx;
      if (w.locked) return;

      w.sum += dx;
      if (Math.abs(w.sum) > 50) {
        step(w.sum > 0 ? 1 : -1);
        w.locked = true;
        w.fired = now;
        w.sum = 0;
      }
    };
  };

  class FsZoom extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;

      this.sectionId = this.dataset.section;
      this.dialog = this.querySelector('.fs-zoom__dialog');
      this.stage = this.querySelector('.fs-zoom__stage');
      this.track = this.querySelector('.fs-zoom__track');
      this.strip = this.querySelector('.fs-zoom__strip');
      this.status = this.querySelector('[data-fs-zoom-status]');
      this.index = 0;
      this.zoomed = false;
      this.scale = 1;
      this.touches = new Map(); // Finger auf der Bühne: pointerId → Position

      this.querySelector('.fs-zoom__close').addEventListener('click', () => this.hide());
      this.querySelector('.fs-zoom__arrow--prev')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('.fs-zoom__arrow--next')?.addEventListener('click', () => this.go(this.index + 1));
      this.strip?.addEventListener('click', (event) => {
        const thumb = event.target.closest('.fs-zoom__thumb');
        if (thumb) this.go(Number(thumb.dataset.index));
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.hide();
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.go(this.index - 1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.go(this.index + 1);
        }
      });

      this.stage.addEventListener('pointerdown', this.onPointerDown.bind(this));
      this.stage.addEventListener('pointermove', this.onPointerMove.bind(this));
      this.stage.addEventListener('pointerup', this.onPointerUp.bind(this));
      this.stage.addEventListener('pointercancel', this.onPointerCancel.bind(this));

      // Trackpad: zwei Finger wischen/schieben/spreizen. passive:false, damit die
      // Zurück-Geste des Browsers und das Seiten-Zoomen nicht mitlaufen.
      this.dialog.addEventListener('wheel', this.onWheel.bind(this), { passive: false });
      // Safari meldet das Spreizen als eigene Gesten-Ereignisse statt als ctrl+wheel
      this.dialog.addEventListener('gesturestart', (event) => event.preventDefault());
      this.dialog.addEventListener('gesturechange', (event) => event.preventDefault());
      this.dialog.addEventListener('gestureend', (event) => {
        event.preventDefault();
        // iOS meldet das Spreizen mit Fingern zusätzlich so – das erledigen schon die Zeiger-Ereignisse
        if (this.pinch || this.touches.size || performance.now() - (this.pinchEndedAt || 0) < 500) return;
        this.pinchTo(event.scale > 1.15 ? 1 : event.scale < 0.87 ? -1 : 0, event.clientX, event.clientY);
      });

      this.track.addEventListener('transitionend', (event) => {
        if (event.target === this.track) this.track.classList.remove('is-released');
      });

      window.addEventListener('resize', () => {
        if (this.zoomed) this.resetZoom(false);
      });

      // Aus der Sektion heraus direkt an body: dort greift keine Sektionsregel auf position.
      if (this.parentElement !== document.body) document.body.appendChild(this);
    }

    get slides() {
      return Array.from(this.track.children);
    }

    get thumbs() {
      return this.strip ? Array.from(this.strip.querySelectorAll('.fs-zoom__thumb')) : [];
    }

    show(opener) {
      this.openedBy = opener;
      const mediaId = opener?.getAttribute('data-media-id');
      const start = Math.max(0, this.slides.findIndex((slide) => slide.dataset.mediaId === mediaId));
      this.startIndex = start;

      this.setAttribute('open', '');
      document.body.classList.add('overflow-hidden');
      window.pauseAllMedia?.();
      this.go(start, false);

      if (typeof window.trapFocus === 'function') window.trapFocus(this, this.dialog);
      else this.dialog.focus();
    }

    hide() {
      if (!this.hasAttribute('open')) return;
      this.resetZoom(false);
      this.removeAttribute('open');
      this.clearDismiss();
      this.touches.clear();
      this.pinch = null;
      this.pointer = null;
      document.body.classList.remove('overflow-hidden');
      window.pauseAllMedia?.();

      // Seitengalerie auf das zuletzt angesehene Bild stellen
      let focusTarget = this.openedBy;
      const mediaId = this.slides[this.index]?.dataset.mediaId;
      const gallery = document.getElementById(`MediaGallery-${this.sectionId}`);
      if (gallery && mediaId && this.index !== this.startIndex && typeof gallery.setActiveMedia === 'function') {
        const key = `${this.sectionId}-${mediaId}`;
        gallery.setActiveMedia(key, false);
        focusTarget = gallery.querySelector(`[data-media-id="${key}"] .product__media-toggle`) || focusTarget;
      }

      if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus();
      focusTarget?.focus({ preventScroll: true });
    }

    go(target, animate = true) {
      const slides = this.slides;
      const count = slides.length;
      if (!count) return;
      const index = ((target % count) + count) % count;
      const changed = index !== this.index;

      if (this.zoomed) this.resetZoom(false);
      this.index = index;
      this.setTrack(0, animate && !reduceMotion.matches);

      slides.forEach((slide, i) => {
        const hidden = i !== index;
        slide.setAttribute('aria-hidden', hidden ? 'true' : 'false');
        slide.inert = hidden;
      });

      this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', i === index ? 'true' : 'false'));
      this.centerThumb(index);

      if (this.status) this.status.textContent = `Bild ${index + 1} von ${count}`;
      if (changed) window.pauseAllMedia?.();
      this.load(index);
    }

    setTrack(offset, animate) {
      if (!animate) this.track.classList.add('is-dragging');
      this.track.style.transform = `translate3d(calc(${-this.index * 100}% + ${offset}px), 0, 0)`;
      if (!animate) {
        void this.track.offsetWidth;
        this.track.classList.remove('is-dragging');
      }
    }

    // Leiste von Hand scrollen – scrollIntoView würde auch die gesperrte Seite bewegen
    centerThumb(index) {
      const thumb = this.thumbs[index];
      if (!thumb || this.strip.scrollWidth <= this.strip.clientWidth) return;
      this.strip.scrollLeft = thumb.offsetLeft - (this.strip.clientWidth - thumb.offsetWidth) / 2;
    }

    // Aktuelles Bild zuerst, die übrigen danach – vorher lädt nichts davon
    load(index) {
      const imageAt = (i) => this.slides[i]?.querySelector('.fs-zoom__img');
      const eager = (img) => {
        if (img && img.loading !== 'eager') img.loading = 'eager';
      };
      const current = imageAt(index);
      eager(current);
      if (current) current.fetchPriority = 'high';

      this.thumbs.forEach((thumb) => eager(thumb.querySelector('img')));

      const rest = () => this.slides.forEach((_, i) => eager(imageAt(i)));
      if (!current || current.complete) rest();
      else {
        current.addEventListener('load', rest, { once: true });
        current.addEventListener('error', rest, { once: true });
      }
    }

    /* ---------- Detail-Zoom ---------- */

    // Zoom-Zustand beginnen: Maße des unvergrößerten Bilds merken, noch ohne Vergrößerung
    beginZoom(img) {
      const box = img.closest('.fs-zoom__slide').getBoundingClientRect();
      const rect = img.getBoundingClientRect();
      this.zoomImg = img;
      this.geo = {
        left: rect.left - box.left,
        top: rect.top - box.top,
        width: rect.width,
        height: rect.height,
        boxWidth: box.width,
        boxHeight: box.height,
      };
      this.tx = 0;
      this.ty = 0;
      this.scale = 1;
      this.zoomed = true;
      img.classList.add('is-zoomed');
    }

    // Bildschirmposition der linken oberen Ecke des unvergrößerten Bilds
    imageOrigin() {
      const box = this.zoomImg.closest('.fs-zoom__slide').getBoundingClientRect();
      return { x: box.left + this.geo.left, y: box.top + this.geo.top };
    }

    zoomAt(img, clientX, clientY, scale = ZOOM) {
      this.beginZoom(img);
      const origin = this.imageOrigin();
      // Der Punkt unter dem Mauszeiger bzw. Finger bleibt, wo er ist
      this.scale = scale;
      this.tx = -(scale - 1) * (clientX - origin.x);
      this.ty = -(scale - 1) * (clientY - origin.y);
      this.sharpen();
      this.applyZoom();
    }

    // Größere Bildfassung anfordern, damit die Vergrößerung scharf bleibt – nur wachsen, nie schrumpfen
    sharpen() {
      const need = Math.ceil(this.geo.width * this.scale);
      if (need > Number(this.zoomImg.dataset.fsSized || 0)) {
        this.zoomImg.sizes = `${need}px`;
        this.zoomImg.dataset.fsSized = String(need);
      }
    }

    clampAxis(t, start, size, box) {
      const scaled = size * this.scale;
      if (scaled <= box) return (box - scaled) / 2 - start;
      return Math.min(-start, Math.max(box - scaled - start, t));
    }

    applyZoom(clamp = true) {
      const g = this.geo;
      if (clamp) {
        this.tx = this.clampAxis(this.tx, g.left, g.width, g.boxWidth);
        this.ty = this.clampAxis(this.ty, g.top, g.height, g.boxHeight);
      }
      this.zoomImg.style.transform = `translate3d(${this.tx}px, ${this.ty}px, 0) scale(${this.scale})`;
    }

    resetZoom(animate = true) {
      if (!this.zoomed || !this.zoomImg) return;
      const img = this.zoomImg;
      if (!animate) img.classList.add('is-panning');
      img.style.transform = '';
      img.classList.remove('is-zoomed');
      if (!animate) {
        void img.offsetWidth;
        img.classList.remove('is-panning');
      }
      this.zoomed = false;
      this.zoomImg = null;
      this.scale = 1;
    }

    /* ---------- Spreizen mit zwei Fingern (2026-10-01) ---------- */

    startPinch() {
      const img = this.slides[this.index]?.querySelector('.fs-zoom__img');
      if (!img) return false;
      // Ein angefangenes Wischen zum Nachbarbild abbrechen
      if (this.pointer?.swipe) {
        this.track.classList.remove('is-dragging');
        this.setTrack(0, !reduceMotion.matches);
      }
      this.pointer = null;
      this.lastTap = null;
      if (!this.zoomed) this.beginZoom(img);

      const [a, b] = [...this.touches.values()];
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const origin = this.imageOrigin();
      // Bildpunkt zwischen den Fingern (in Bildkoordinaten) – er bleibt beim Spreizen unter den Fingern
      this.pinch = {
        dist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        scale: this.scale,
        ux: (mid.x - origin.x - this.tx) / this.scale,
        uy: (mid.y - origin.y - this.ty) / this.scale,
        mid,
      };
      this.zoomImg.classList.add('is-panning');
      return true;
    }

    movePinch() {
      const p = this.pinch;
      if (!this.zoomImg) return;
      const [a, b] = [...this.touches.values()];
      let scale = p.scale * (Math.hypot(a.x - b.x, a.y - b.y) / p.dist);
      // Über die Grenzen hinaus nur gebremst – beim Loslassen federt es zurück
      if (scale > ZOOM_MAX) scale = ZOOM_MAX + (scale - ZOOM_MAX) * 0.3;
      if (scale < 1) scale = Math.max(0.6, 1 - (1 - scale) * 0.4);
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const origin = this.imageOrigin();
      this.scale = scale;
      this.tx = mid.x - origin.x - scale * p.ux;
      this.ty = mid.y - origin.y - scale * p.uy;
      p.mid = mid;
      this.applyZoom(false);
    }

    endPinch() {
      const p = this.pinch;
      this.pinch = null;
      this.pinchEndedAt = performance.now();
      const img = this.zoomImg;
      if (!img) return;
      img.classList.remove('is-panning');

      // Kaum vergrößert: zurück auf normal
      if (this.scale < 1.1) {
        this.resetZoom();
        this.pointer = null;
        return;
      }
      if (this.scale > ZOOM_MAX) {
        const origin = this.imageOrigin();
        this.scale = ZOOM_MAX;
        this.tx = p.mid.x - origin.x - ZOOM_MAX * p.ux;
        this.ty = p.mid.y - origin.y - ZOOM_MAX * p.uy;
      }
      this.sharpen();
      this.applyZoom(); // an die Ränder setzen – gleitet dank Übergang statt zu springen

      // Bleibt ein Finger liegen, verschiebt er das Bild weiter
      const rest = [...this.touches.entries()][0];
      this.pointer = rest
        ? { id: rest[0], type: 'touch', img, x: rest[1].x, y: rest[1].y, tx: this.tx, ty: this.ty, moved: false, swipe: false, afterPinch: true }
        : null;
    }

    /* ---------- Runterziehen zum Schließen (Finger, 2026-10-01) ---------- */

    startDismiss() {
      this.dismissEl = this.slides[this.index]?.firstElementChild || null;
      this.dismissTrack = [];
      this.dismissX = 0;
      this.dismissY = 0;
      clearTimeout(this.settleTimer);
      this.dialog.classList.remove('is-settling');
      this.dialog.classList.add('is-dismissing');
    }

    moveDismiss(dx, dy, time) {
      // Nach oben nur gebremst
      const y = dy > 0 ? dy : dy * 0.25;
      const progress = Math.min(1, Math.max(0, y) / (this.stage.clientHeight * 0.6));
      if (this.dismissEl) this.dismissEl.style.transform = `translate3d(${dx}px, ${y}px, 0) scale(${1 - progress * 0.2})`;
      // Papierfläche wird durchscheinend, dahinter wird die Produktseite sichtbar
      this.dialog.style.backgroundColor = `color-mix(in srgb, var(--fs-paper) ${Math.round((1 - progress) * 100)}%, transparent)`;
      this.dialog.style.setProperty('--fs-zoom-dismiss', progress.toFixed(3));
      this.dismissX = dx;
      this.dismissY = y;
      this.dismissTrack.push({ y, time });
      if (this.dismissTrack.length > 5) this.dismissTrack.shift();
    }

    endDismiss(cancel = false) {
      const y = this.dismissY;
      const t = this.dismissTrack;
      const speed = t.length > 1 ? (t[t.length - 1].y - t[0].y) / Math.max(1, t[t.length - 1].time - t[0].time) : 0;
      const close = !cancel && (y > this.stage.clientHeight / 6 || (speed > 0.5 && y > 30));
      this.dialog.classList.remove('is-dismissing');
      if (close) this.dismissClose();
      else this.dismissBack();
    }

    dismissBack() {
      this.dialog.classList.add('is-settling');
      if (this.dismissEl) {
        this.dismissEl.style.transition = 'transform 0.3s cubic-bezier(0.2, 0.7, 0.25, 1)';
        this.dismissEl.style.transform = '';
      }
      this.dialog.style.backgroundColor = '';
      this.dialog.style.removeProperty('--fs-zoom-dismiss');
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.clearDismiss(), 320);
    }

    dismissClose() {
      if (reduceMotion.matches || !this.dismissEl) {
        this.hide();
        return;
      }
      this.dialog.classList.add('is-settling');
      this.dismissEl.style.transition = 'transform 0.22s cubic-bezier(0.4, 0, 1, 1)';
      this.dismissEl.style.transform = `translate3d(${this.dismissX}px, ${this.stage.clientHeight}px, 0) scale(0.8)`;
      this.dialog.style.backgroundColor = 'transparent';
      this.dialog.style.setProperty('--fs-zoom-dismiss', '1');
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.hide(), 220);
    }

    clearDismiss() {
      clearTimeout(this.settleTimer);
      this.dialog.classList.remove('is-dismissing', 'is-settling');
      this.dialog.style.backgroundColor = '';
      this.dialog.style.removeProperty('--fs-zoom-dismiss');
      if (this.dismissEl) {
        this.dismissEl.style.transition = '';
        this.dismissEl.style.transform = '';
      }
      this.dismissEl = null;
      this.dismissY = 0;
    }

    /* ---------- Zeiger: Klick, Ziehen, Wischen, Doppeltippen, Spreizen, Runterziehen ---------- */

    onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('button, a, deferred-media, product-model, video, iframe')) return;
      try {
        this.stage.setPointerCapture(event.pointerId);
      } catch (error) {
        // Zeiger schon losgelassen – dann eben ohne Capture
      }

      if (event.pointerType === 'touch') {
        this.touches.set(event.pointerId, { x: event.clientX, y: event.clientY });
        // Zweiter Finger: Spreizen statt Wischen/Verschieben (nicht mitten im Runterziehen)
        if (this.touches.size === 2 && !this.pointer?.dismiss && this.startPinch()) return;
        if (this.touches.size > 1) return;
      }

      this.pointer = {
        id: event.pointerId,
        type: event.pointerType,
        // Nach setPointerCapture zeigt event.target beim Loslassen auf die Bühne, nicht aufs Bild
        img: event.target.closest('.fs-zoom__img'),
        x: event.clientX,
        y: event.clientY,
        tx: this.tx,
        ty: this.ty,
        moved: false,
        swipe: false,
        dismiss: false,
      };
    }

    onPointerMove(event) {
      if (event.pointerType === 'touch' && this.touches.has(event.pointerId)) {
        this.touches.set(event.pointerId, { x: event.clientX, y: event.clientY });
        if (this.pinch) {
          if (this.touches.size >= 2) this.movePinch();
          return;
        }
      }

      const p = this.pointer;
      if (!p || event.pointerId !== p.id) return;
      const dx = event.clientX - p.x;
      const dy = event.clientY - p.y;

      if (!p.moved) {
        if (Math.hypot(dx, dy) < 6) return;
        p.moved = true;
        if (this.zoomed) this.zoomImg.classList.add('is-panning');
        else if (Math.abs(dx) > Math.abs(dy) && this.slides.length > 1) {
          p.swipe = true;
          this.track.classList.add('is-dragging');
        } else if (p.type !== 'mouse' && dy > 0 && Math.abs(dy) >= Math.abs(dx)) {
          p.dismiss = true;
          this.startDismiss();
        }
      }

      if (this.zoomed) {
        this.tx = p.tx + dx;
        this.ty = p.ty + dy;
        this.applyZoom();
      } else if (p.swipe) {
        this.track.style.transform = `translate3d(calc(${-this.index * 100}% + ${dx}px), 0, 0)`;
      } else if (p.dismiss) {
        this.moveDismiss(dx, dy, event.timeStamp);
      }
    }

    onPointerUp(event) {
      if (event.pointerType === 'touch') {
        this.touches.delete(event.pointerId);
        if (this.pinch) {
          if (this.touches.size < 2) this.endPinch();
          return;
        }
      }

      const p = this.pointer;
      if (!p || event.pointerId !== p.id) return;
      this.pointer = null;

      if (p.moved) {
        if (p.swipe) {
          this.track.classList.add('is-released');
          this.track.classList.remove('is-dragging');
          const dx = event.clientX - p.x;
          const threshold = Math.min(80, this.stage.clientWidth * 0.15);
          if (Math.abs(dx) > threshold) this.go(this.index + (dx < 0 ? 1 : -1));
          else this.setTrack(0, !reduceMotion.matches);
        } else if (p.dismiss) {
          this.endDismiss();
        } else if (this.zoomed) {
          this.zoomImg.classList.remove('is-panning');
        }
        return;
      }

      // Der übrig gebliebene Finger nach dem Spreizen ist kein Tippen
      if (p.afterPinch) return;

      const img = p.img;

      if (p.type === 'mouse') {
        if (this.zoomed) this.resetZoom();
        else if (img) this.zoomAt(img, event.clientX, event.clientY);
        else this.hide();
        return;
      }

      // Finger/Stift: Doppeltippen schaltet den Zoom
      const last = this.lastTap;
      if (last && event.timeStamp - last.time < 320 && Math.hypot(event.clientX - last.x, event.clientY - last.y) < 30) {
        this.lastTap = null;
        if (this.zoomed) this.resetZoom();
        else if (img) this.zoomAt(img, event.clientX, event.clientY);
      } else {
        this.lastTap = { time: event.timeStamp, x: event.clientX, y: event.clientY };
      }
    }

    onPointerCancel(event) {
      if (event?.pointerType === 'touch') this.touches.delete(event.pointerId);
      if (this.pinch) {
        if (this.touches.size < 2) this.endPinch();
        return;
      }
      if (!this.pointer) return;
      if (this.pointer.swipe) {
        this.track.classList.remove('is-dragging');
        this.setTrack(0, !reduceMotion.matches);
      }
      if (this.pointer.dismiss) this.endDismiss(true);
      this.zoomImg?.classList.remove('is-panning');
      this.pointer = null;
    }

    /* ---------- Trackpad und Mausrad ---------- */

    onWheel(event) {
      // Läuft die Vorschau-Leiste über (Handy, viele Bilder), scrollt sie selbst
      if (this.strip?.contains(event.target) && this.strip.scrollWidth > this.strip.clientWidth) return;
      event.preventDefault();

      const { dx, dy } = wheelDelta(event, this.stage.clientWidth);

      // Zwei Finger spreizen/zusammenziehen: Chrome und Firefox melden das als ctrl+wheel
      if (event.ctrlKey) {
        this.pinchSum = (this.pinchSum || 0) + dy;
        clearTimeout(this.pinchTimer);
        this.pinchTimer = setTimeout(() => (this.pinchSum = 0), 200);
        if (Math.abs(this.pinchSum) > 30) {
          this.pinchTo(this.pinchSum < 0 ? 1 : -1, event.clientX, event.clientY);
          this.pinchSum = 0;
        }
        return;
      }

      // Im Zoom: zwei Finger verschieben das Bild
      if (this.zoomed) {
        this.zoomImg.classList.add('is-panning');
        this.tx -= dx;
        this.ty -= dy;
        this.applyZoom();
        clearTimeout(this.panTimer);
        this.panTimer = setTimeout(() => this.zoomImg?.classList.remove('is-panning'), 150);
        return;
      }

      // Sonst: waagrecht wischen = ein Bild weiter
      if (Math.abs(dx) <= Math.abs(dy) || this.slides.length < 2) return;
      this.swipe ||= wheelSwiper((dir) => this.go(this.index + dir));
      this.swipe(dx, event.timeStamp);
    }

    // direction 1 = vergrößern, -1 = verkleinern
    pinchTo(direction, clientX, clientY) {
      if (direction > 0 && !this.zoomed) {
        const img = this.slides[this.index]?.querySelector('.fs-zoom__img');
        if (!img) return;
        const rect = img.getBoundingClientRect();
        const x = Number.isFinite(clientX) ? clientX : rect.left + rect.width / 2;
        const y = Number.isFinite(clientY) ? clientY : rect.top + rect.height / 2;
        this.zoomAt(img, x, y);
      } else if (direction < 0 && this.zoomed) {
        this.resetZoom();
      }
    }
  }

  customElements.define('fs-zoom', FsZoom);

  /* ---------- Seitengalerie: Pfeile (Desktop + Handy), Trackpad und weicher Wechsel (Desktop) ---------- */

  class FsGalleryNav extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.gallery = this.closest('media-gallery');
      this.list = this.gallery?.querySelector('.product__media-list');
      if (!this.gallery || !this.list) return;
      this.bound = true;

      this.desktop = window.matchMedia('(min-width: 750px)');
      this.current = this.activeItem();

      this.querySelector('.fs-gallery-nav__btn--prev').addEventListener('click', () => this.onArrow(-1));
      this.querySelector('.fs-gallery-nav__btn--next').addEventListener('click', () => this.onArrow(1));

      // Handy: Ein Wisch, der auf einem Pfeil beginnt, erreicht die Bildleiste nicht (die Pfeile liegen
      // daneben, nicht darin) – hier selbst auswerten, damit Wischen überall auf dem Bild wirkt.
      this.addEventListener(
        'touchstart',
        (event) => {
          const touch = event.changedTouches[0];
          this.touch = { x: touch.clientX, y: touch.clientY };
        },
        { passive: true }
      );
      this.addEventListener(
        'touchend',
        (event) => {
          if (!this.touch) return;
          const touch = event.changedTouches[0];
          const dx = touch.clientX - this.touch.x;
          const dy = touch.clientY - this.touch.y;
          this.touch = null;
          if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
          this.swipedAt = event.timeStamp;
          this.step(dx < 0 ? 1 : -1);
        },
        { passive: true }
      );
      // Wischt der Finger selbst auf dem Bild, gilt das gemerkte Ziel eines Pfeil-Tipps nicht mehr
      this.list.addEventListener('touchstart', () => (this.pending = null), { passive: true });

      // Pfeile mittig zum Bild, nicht zum ganzen Block: am Desktop steht darunter noch der Zähler „1 / 4"
      // (sonst saßen sie 35 px zu tief). Beobachtet Bild und Block – der Zähler erscheint erst, wenn die
      // Pfeile sichtbar werden (fs-gallery-nav[hidden] + .slider-buttons), und Hoch-/Querformat wechseln.
      const fit = () => {
        const bottom = this.parentElement.getBoundingClientRect().bottom - this.list.getBoundingClientRect().bottom;
        this.style.bottom = `${Math.max(0, Math.round(bottom))}px`;
      };
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(fit);
        observer.observe(this.list);
        observer.observe(this.parentElement);
      }
      this.desktop.addEventListener('change', fit);

      // Nur waagrechtes Wischen abfangen – senkrechtes Scrollen bleibt beim Browser
      this.swipe = wheelSwiper((dir) => this.step(dir));
      // Auf dem ganzen Bildbereich, auch wenn der Zeiger gerade über einem Pfeil steht
      this.parentElement.addEventListener(
        'wheel',
        (event) => {
          if (!this.desktop.matches || event.ctrlKey) return;
          const { dx, dy } = wheelDelta(event, this.list.clientWidth);
          if (Math.abs(dx) <= Math.abs(dy)) return;
          event.preventDefault();
          this.swipe(dx, event.timeStamp);
        },
        { passive: false }
      );

      // iPad & Co. (ab 750 px, Finger oder Stift): Bild folgt dem Finger wie am Handy, Loslassen blättert
      // weiter oder federt zurück. Maus bleibt bei Pfeilen/Trackpad, unter 750 px wischt Dawns Slider.
      // Touch-Ereignisse statt Pointer-Ereignissen: Safari bricht Pointer mit pointercancel ab, sobald es die
      // Bewegung als eigene Geste übernimmt – erste Fassung lief am echten iPad deshalb nicht (2026-10-02).
      // touchmove ist nicht passiv, damit waagrechtes Wischen die Systemgeste per preventDefault abhält.
      this.list.addEventListener('touchstart', this.onDragStart.bind(this), { passive: true });
      this.list.addEventListener('touchmove', this.onDragMove.bind(this), { passive: false });
      this.list.addEventListener('touchend', this.onDragEnd.bind(this));
      this.list.addEventListener('touchcancel', this.onDragEnd.bind(this));
      // Nach einem Wisch kein Zoom-Fenster durch den nachfolgenden Klick
      this.list.addEventListener(
        'click',
        (event) => {
          if (this.draggedAt && performance.now() - this.draggedAt < 500) {
            event.preventDefault();
            event.stopPropagation();
          }
        },
        true
      );

      // Zähler „1 / 4" unter dem Bild: Dawn zählt nur beim Scrollen der Handy-Leiste mit, am Desktop
      // wird nicht gescrollt – hier nachführen. Seine kleinen Pfeile blättern am Desktop ebenfalls.
      const sliderButtons = this.parentElement.querySelector('.slider-buttons');
      this.counter = sliderButtons?.querySelector('.slider-counter--current');
      this.total = sliderButtons?.querySelector('.slider-counter--total');
      // Dawns Seitenrechnung zählt alle sichtbaren Bilder und teilt durch ihren Abstand. Während eines
      // Wechsels liegen am Desktop zwei übereinander (Abstand 0) – feuert dann sein ResizeObserver (Höhe
      // ändert sich beim Wechsel), steht dort „2 / -Infinity" (am iPad gesehen, 2026-10-02). Am Desktop
      // zählt deshalb nur fs-gallery-nav; unter 750 px rechnet Dawn wie gewohnt.
      const slider = this.list.closest('slider-component');
      if (slider && typeof slider.initPages === 'function') {
        const initPages = slider.initPages.bind(slider);
        slider.initPages = () => (this.desktop.matches ? this.updateCounter() : initPages());
      }
      sliderButtons?.addEventListener(
        'click',
        (event) => {
          const button = event.target.closest('button[name="previous"], button[name="next"]');
          if (!button || !this.desktop.matches) return;
          event.stopPropagation(); // Dawns Scroll-Handler am Knopf läuft am Desktop ins Leere
          this.step(button.name === 'next' ? 1 : -1);
        },
        true
      );
      this.desktop.addEventListener('change', () => this.updateCounter());
      this.updateCounter();

      // Jeden Wechsel animieren, egal wer ihn auslöst: Pfeile, Trackpad oder Dawns Vorschaubilder
      new MutationObserver(this.onMutations.bind(this)).observe(this.list, {
        attributes: true,
        attributeFilter: ['class'],
        subtree: true,
        childList: true,
      });

      this.hidden = false;
      fit();
    }

    get items() {
      return Array.from(this.list.children).filter((item) => item.matches('.product__media-item'));
    }

    activeItem() {
      return this.list.querySelector(':scope > .product__media-item.is-active');
    }

    onArrow(direction) {
      // Nach einem Wisch auf dem Pfeil kein zweiter Schritt durch den nachfolgenden Klick
      if (this.swipedAt && performance.now() - this.swipedAt < 500) return;
      this.step(direction);
    }

    step(direction) {
      const items = this.items;
      if (items.length < 2) return;
      if (!this.desktop.matches) {
        this.scrollStep(direction);
        return;
      }
      const index = items.indexOf(this.activeItem());
      const target = items[(index + direction + items.length) % items.length];
      this.direction = direction;
      this.activate(target);
    }

    // Handy: Dawns Wisch-Slider eine Folie weiter, am Ende zurück zum ersten Bild wie am Desktop.
    // Gleitet nativ wie ein Fingerwisch; Vorschaubilder rücken über Dawns slideChanged nach.
    scrollStep(direction) {
      const items = this.items.filter((item) => item.clientWidth > 0);
      if (items.length < 2) return;
      const left = this.list.scrollLeft;
      let index = 0;
      items.forEach((item, i) => {
        if (Math.abs(item.offsetLeft - left) < Math.abs(items[index].offsetLeft - left)) index = i;
      });
      // Zweimal schnell getippt: Das Bild gleitet noch – vom Ziel aus weiterzählen, nicht vom Zwischenstand
      const pending = this.pending;
      if (pending && performance.now() - pending.at < 800 && items[pending.index]) index = pending.index;
      const targetIndex = (index + direction + items.length) % items.length;
      const target = items[targetIndex];
      this.pending = { index: targetIndex, at: performance.now() };
      this.list.scrollTo({ left: target.offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      this.announce(target);
    }

    thumbnailFor(item) {
      return this.gallery.elements?.thumbnails?.querySelector(`[data-target="${item.dataset.mediaId}"]`);
    }

    announce(item) {
      const thumbnail = this.thumbnailFor(item);
      if (thumbnail && typeof this.gallery.announceLiveRegion === 'function') {
        this.gallery.announceLiveRegion(item, thumbnail.dataset.mediaPosition);
      }
    }

    // Wie Dawns setActiveMedia, aber ohne die Seite zum Bild zu scrollen
    activate(item) {
      const gallery = this.gallery;
      this.items.forEach((element) => element.classList.toggle('is-active', element === item));
      const thumbnail = this.thumbnailFor(item);
      if (thumbnail && typeof gallery.setActiveThumbnail === 'function') gallery.setActiveThumbnail(thumbnail);
      this.announce(item);
      if (typeof gallery.playActiveMedia === 'function') gallery.playActiveMedia(item);
    }

    // Der Finger, mit dem der Wisch begann (bei mehreren Fingern zählt nur er)
    dragTouch(event) {
      const drag = this.drag;
      return drag && Array.from(event.changedTouches).find((touch) => touch.identifier === drag.id);
    }

    onDragStart(event) {
      if (!this.desktop.matches || this.items.length < 2) return;
      // Zweiter Finger = Spreizen/Zoomen der Seite: laufenden Wisch zurückfedern lassen
      if (event.touches.length > 1) {
        if (this.drag) this.endDrag(true);
        return;
      }
      const touch = event.changedTouches[0];
      this.drag = { id: touch.identifier, x: touch.clientX, y: touch.clientY, dx: 0, axis: null, samples: [] };
    }

    onDragMove(event) {
      const drag = this.drag;
      const touch = this.dragTouch(event);
      if (!touch) return;
      if (event.touches.length > 1) {
        this.endDrag(true);
        return;
      }
      const dx = touch.clientX - drag.x;
      const dy = touch.clientY - drag.y;
      if (!drag.axis) {
        // Schon vor der Entscheidung: eher waagrecht → Safari darf die Bewegung nicht als Geste übernehmen
        if (Math.abs(dx) > Math.abs(dy) && event.cancelable) event.preventDefault();
        if (Math.hypot(dx, dy) < 8) return;
        // Senkrecht = Seite scrollen (touch-action: pan-y in fs-pdp-zoom.css), das bleibt beim Browser
        if (Math.abs(dy) >= Math.abs(dx)) {
          this.drag = null;
          return;
        }
        drag.axis = 'x';
        this.stopAnimation();
        drag.active = this.activeItem();
        if (!drag.active) {
          this.drag = null;
          return;
        }
        this.list.classList.add('fs-gallery-animating');
      }
      if (event.cancelable) event.preventDefault();
      drag.dx = dx;
      drag.samples.push({ x: touch.clientX, t: event.timeStamp });
      if (drag.samples.length > 5) drag.samples.shift();

      // Nachbarbild je nach Richtung daneben zeigen – Dawn blendet inaktive Bilder sonst aus
      const width = this.list.clientWidth;
      const items = this.items;
      const dir = dx < 0 ? 1 : -1;
      const neighbour = items[(items.indexOf(drag.active) + dir + items.length) % items.length];
      if (drag.neighbour !== neighbour) {
        if (drag.neighbour) {
          drag.neighbour.classList.remove('fs-gallery-leaving');
          drag.neighbour.style.transform = '';
        }
        neighbour.classList.add('fs-gallery-leaving');
        drag.neighbour = neighbour;
      }
      drag.active.style.transform = `translateX(${dx}px)`;
      neighbour.style.transform = `translateX(${dx + dir * width}px)`;
    }

    onDragEnd(event) {
      if (!this.dragTouch(event)) return;
      this.endDrag(event.type === 'touchcancel');
    }

    // Loslassen: weiterblättern (weit genug oder schnell genug gewischt) oder zurückfedern
    endDrag(cancel) {
      const drag = this.drag;
      if (!drag) return;
      this.drag = null;
      if (drag.axis !== 'x' || !drag.neighbour) return;
      this.draggedAt = performance.now();

      const { active, neighbour, dx, samples } = drag;
      const width = this.list.clientWidth;
      const dir = dx < 0 ? 1 : -1;
      const first = samples[0];
      const last = samples[samples.length - 1];
      const speed = first && last ? (last.x - first.x) / Math.max(1, last.t - first.t) : 0; // px/ms
      const flick = Math.abs(speed) > 0.4 && Math.sign(speed) === Math.sign(dx);
      const commit = !cancel && (Math.abs(dx) > width * 0.2 || flick);
      // Ausrollen wie nach einem Wisch im Zoom-Fenster (.is-released): erst schnell, dann weich
      const timing = { duration: reduceMotion.matches ? 0 : 380, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' };

      let leaving = neighbour;
      let frames;
      if (commit) {
        // Das Nachbarbild wird das aktive; der MutationObserver soll dafür nicht noch einmal animieren
        neighbour.classList.remove('fs-gallery-leaving');
        active.classList.add('fs-gallery-leaving');
        leaving = active;
        this.skipNextAnimation = true;
        this.activate(neighbour);
        frames = [
          [active, `translateX(${dx}px)`, `translateX(${-dir * width}px)`],
          [neighbour, `translateX(${dx + dir * width}px)`, 'translateX(0)'],
        ];
      } else {
        frames = [
          [active, `translateX(${dx}px)`, 'translateX(0)'],
          [neighbour, `translateX(${dx + dir * width}px)`, `translateX(${dir * width}px)`],
        ];
      }

      this.leaving = leaving;
      this.running = frames.map(([element, from, to]) => {
        element.style.transform = '';
        return typeof element.animate === 'function'
          ? element.animate([{ transform: from }, { transform: to }], timing)
          : null;
      });
      const running = this.running;
      const done = running.find(Boolean);
      if (!done) {
        this.stopAnimation();
        return;
      }
      done.finished.then(
        () => {
          if (this.running === running) this.stopAnimation();
        },
        () => {}
      );
    }

    onMutations(records) {
      // Nur Klassenwechsel der Bilder selbst und Umsortieren der Liste zählen
      const own = records.filter((record) => record.target === this.list || record.target.parentElement === this.list);
      if (!own.length) return;
      const previous = this.current;
      const next = this.activeItem();
      this.current = next;
      const direction = this.direction;
      this.direction = 0;
      this.updateCounter();
      // Fingerwisch am iPad animiert selbst (onDragEnd)
      if (this.skipNextAnimation) {
        this.skipNextAnimation = false;
        return;
      }

      // Variantenwechsel sortiert die Liste um – dann ohne Animation
      if (own.some((record) => record.type === 'childList')) return;
      if (!this.desktop.matches || reduceMotion.matches) return;
      if (!previous || !next || previous === next || !previous.isConnected) return;

      const items = this.items;
      this.animate(previous, next, direction || Math.sign(items.indexOf(next) - items.indexOf(previous)) || 1);
    }

    animate(previous, next, direction) {
      this.stopAnimation();
      if (typeof next.animate !== 'function') return;

      const distance = this.list.clientWidth;
      this.list.classList.add('fs-gallery-animating');
      previous.classList.add('fs-gallery-leaving');
      this.leaving = previous;

      this.running = [
        previous.animate(
          [
            { transform: 'translateX(0)', opacity: 1 },
            { transform: `translateX(${-direction * distance}px)`, opacity: 0 },
          ],
          SLIDE
        ),
        next.animate(
          [
            { transform: `translateX(${direction * distance}px)`, opacity: 0 },
            { transform: 'translateX(0)', opacity: 1 },
          ],
          SLIDE
        ),
      ];

      const running = this.running;
      running[1].finished.then(
        () => {
          if (this.running === running) this.stopAnimation();
        },
        () => {}
      );
    }

    updateCounter() {
      if (!this.counter || !this.desktop.matches) return;
      const index = this.items.indexOf(this.activeItem());
      if (index >= 0) this.counter.textContent = String(index + 1);
      if (this.total) this.total.textContent = String(this.items.length);
    }

    stopAnimation() {
      if (this.running) this.running.forEach((animation) => animation?.cancel());
      this.running = null;
      this.leaving?.classList.remove('fs-gallery-leaving');
      this.leaving = null;
      this.list.classList.remove('fs-gallery-animating');
    }
  }

  customElements.define('fs-gallery-nav', FsGalleryNav);
})();
